import { Plus, Trash2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { type HeaderRow, MAX_CUSTOM_HEADERS, newHeaderRowKey } from '../utils';

interface CustomHeadersEditorProps {
  rows: HeaderRow[];
  onChange: (rows: HeaderRow[]) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

/**
 * Custom request headers, write-only. Stored values are never returned, so a
 * stored header shows its name and a blank value: leaving it blank keeps the
 * stored value, typing replaces it. Names of stored headers are fixed — remove
 * the row and add a new one to rename.
 */
export function CustomHeadersEditor({
  rows,
  onChange,
  errors = {},
  disabled = false,
}: CustomHeadersEditorProps) {
  const atLimit = rows.length >= MAX_CUSTOM_HEADERS;
  const update = (key: string, patch: Partial<HeaderRow>) =>
    onChange(rows.map(row => (row.key === key ? { ...row, ...patch } : row)));

  return (
    <div className="space-y-2">
      {rows.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No custom headers. Add one to send a static credential your receiver expects.
        </p>
      )}
      {rows.map(row => (
        <div key={row.key} className="space-y-1">
          <div className="flex items-start gap-2">
            <Input
              aria-label="Header name"
              value={row.name}
              onChange={e => update(row.key, { name: e.target.value })}
              placeholder="X-Api-Key"
              className="w-2/5 font-mono text-sm"
              disabled={disabled || row.existing}
              autoComplete="off"
            />
            <Input
              aria-label={`Value for ${row.name || 'header'}`}
              type="password"
              value={row.value}
              onChange={e => update(row.key, { value: e.target.value })}
              placeholder={row.existing ? '•••••••• (unchanged)' : 'Value'}
              className="flex-1 font-mono text-sm"
              disabled={disabled}
              autoComplete="new-password"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0"
              onClick={() => onChange(rows.filter(r => r.key !== row.key))}
              disabled={disabled}
              aria-label={`Remove header ${row.name}`}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
          {errors[row.key] && <p className="text-xs text-destructive">{errors[row.key]}</p>}
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          onChange([...rows, { key: newHeaderRowKey(), name: '', value: '', existing: false }])
        }
        disabled={disabled || atLimit}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add header
      </Button>
      {atLimit && (
        <p className="text-xs text-muted-foreground">
          An endpoint can have at most {MAX_CUSTOM_HEADERS} custom headers.
        </p>
      )}
    </div>
  );
}
